import React, { useState, useEffect } from 'react';
import './AccelerateIntegration.css';

function AccelerateIntegration({ onBack }) {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Multi-provider AI data structure (like Residential Refi module)
  const [caseData, setCaseData] = useState({
    openai: {},
    claude: {},
    gemini: {}
  });

  // Final form values (consensus or user-edited)
  const [formData, setFormData] = useState({
    // Agent & Property Info
    agentFileNumber: '',
    propertyAddress1: '',
    propertyAddress2: '',
    propertyZip: '',
    propertyCity: '',
    propertyState: '',
    propertyCounty: '',
    propertyType: 'Residential',

    // Title Agency (pre-filled)
    titleAgency: 'Sun Title Agency of Michigan LLC - 385 Leonard Street NE, Grand Rapids, MI 49503',

    // Party 1 (Borrower)
    party1Role: 'Borrower',
    party1Type: 'Individual',
    party1FirstName: '',
    party1MiddleName: '',
    party1LastName: '',
    party1Suffix: '',
    party1Address1: '',
    party1Address2: '',
    party1Zip: '',
    party1City: '',
    party1State: '',
    party1County: '',
    sameAsPropertyAddress: false,

    // Product Info
    productType: 'Accelerate Title Report',
    transactionType: 'Refinance',
    loanAmount: ''
  });

  // Track which AI providers are selected for consensus
  const [selectedProviders, setSelectedProviders] = useState(['openai', 'claude', 'gemini']);

  // Track user edits (for blue highlighting)
  const [editedFields, setEditedFields] = useState(new Set());

  // Track expanded fields for hover-to-expand
  const [expandedFields, setExpandedFields] = useState(new Set());

  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showDataTraceSection, setShowDataTraceSection] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Helper: Check if value is empty
  const isValueEmpty = (value) => {
    return value === null || value === undefined || value === '' || value === 'N/A';
  };

  // Check if all selected AI providers agree on a field value
  const checkConsensus = (fieldId) => {
    if (selectedProviders.length === 0) return true;

    const values = selectedProviders.map(provider => {
      // Map formData field names to AI extraction field names
      const aiFieldMap = {
        propertyAddress1: 'PropertyAddress1',
        propertyAddress2: 'PropertyAddress2',
        propertyCity: 'PropertyCity',
        propertyState: 'PropertyState',
        propertyZip: 'PropertyZip',
        propertyCounty: 'PropertyCounty',
        party1FirstName: 'Borrower1FirstName',
        party1MiddleName: 'Borrower1MiddleName',
        party1LastName: 'Borrower1LastName',
        party1Suffix: 'Borrower1Suffix',
        loanAmount: 'LoanAmount'
      };

      const aiField = aiFieldMap[fieldId] || fieldId;
      return caseData[provider][aiField];
    });

    const nonEmptyValues = values.filter(v => !isValueEmpty(v));
    if (nonEmptyValues.length === 0) return false;

    const uniqueValues = [...new Set(nonEmptyValues)];
    return uniqueValues.length === 1;
  };

  // Get most common value across AI providers
  const getMostCommonValue = (fieldId) => {
    if (selectedProviders.length === 0) return '';

    const values = selectedProviders.map(provider => {
      const aiFieldMap = {
        propertyAddress1: 'PropertyAddress1',
        propertyAddress2: 'PropertyAddress2',
        propertyCity: 'PropertyCity',
        propertyState: 'PropertyState',
        propertyZip: 'PropertyZip',
        propertyCounty: 'PropertyCounty',
        party1FirstName: 'Borrower1FirstName',
        party1MiddleName: 'Borrower1MiddleName',
        party1LastName: 'Borrower1LastName',
        party1Suffix: 'Borrower1Suffix',
        loanAmount: 'LoanAmount'
      };

      const aiField = aiFieldMap[fieldId] || fieldId;
      return caseData[provider][aiField];
    });

    const nonEmptyValues = values.filter(v => !isValueEmpty(v));
    if (nonEmptyValues.length === 0) return '';

    const valueCounts = nonEmptyValues.reduce((acc, value) => {
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});

    const sortedValues = Object.entries(valueCounts).sort((a, b) => b[1] - a[1]);
    return sortedValues.length > 0 ? sortedValues[0][0] : '';
  };

  // Get CSS class for input based on consensus state
  const getInputClass = (fieldId) => {
    // Only apply consensus coloring if we have AI data
    const hasAIData = Object.keys(caseData.openai).length > 0 ||
                      Object.keys(caseData.claude).length > 0 ||
                      Object.keys(caseData.gemini).length > 0;

    if (!hasAIData) {
      return '';
    }

    if (editedFields.has(fieldId)) {
      return 'edited-input';
    }

    const hasConsensus = checkConsensus(fieldId);
    const value = formData[fieldId];

    if (isValueEmpty(value)) {
      return '';
    }

    console.log(`Field ${fieldId}: consensus=${hasConsensus}, value="${value}"`);

    if (hasConsensus) {
      return 'consensus-input';
    } else {
      return 'no-consensus-input';
    }
  };

  // Toggle provider selection
  const toggleProvider = (provider) => {
    setSelectedProviders(prev => {
      if (prev.includes(provider)) {
        return prev.filter(p => p !== provider);
      } else {
        return [...prev, provider];
      }
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Mark field as edited (for blue highlighting)
    setEditedFields(prev => new Set([...prev, field]));
  };

  const handleSameAsPropertyToggle = () => {
    const newValue = !formData.sameAsPropertyAddress;
    setFormData(prev => ({
      ...prev,
      sameAsPropertyAddress: newValue,
      ...(newValue ? {
        party1Address1: prev.propertyAddress1,
        party1Address2: prev.propertyAddress2,
        party1Zip: prev.propertyZip,
        party1City: prev.propertyCity,
        party1State: prev.propertyState,
        party1County: prev.propertyCounty
      } : {})
    }));
  };

  // File upload handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setUploadedFile(file);
      } else {
        alert('Please upload a PDF file');
      }
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setUploadedFile(file);
      } else {
        alert('Please upload a PDF file');
      }
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  const handleAIAutoFill = async () => {
    if (!uploadedFile) {
      alert('Please upload a PDF first');
      return;
    }

    setIsAnalyzing(true);
    try {
      // Call the same PDF analysis endpoint used by Residential Refi module
      const formDataObj = new FormData();
      formDataObj.append('file', uploadedFile);
      formDataObj.append('models', JSON.stringify(['claude', 'openai', 'gemini']));
      formDataObj.append('instructions', '');

      const response = await fetch('/api/analysis/analyze-pdf', {
        method: 'POST',
        body: formDataObj
      });

      if (!response.ok) {
        throw new Error('PDF analysis failed');
      }

      const result = await response.json();
      console.log('Analysis result:', result);

      // Store multi-provider AI data
      if (result.data) {
        const newCaseData = {
          openai: result.data.openai || {},
          claude: result.data.claude || {},
          gemini: result.data.gemini || {}
        };
        console.log('Setting caseData:', newCaseData);
        setCaseData(newCaseData);

        // Auto-fill form fields using consensus values
        setFormData(prev => ({
          ...prev,
          propertyAddress1: getMostCommonValueFromResult(result.data, 'PropertyAddress1') || prev.propertyAddress1,
          propertyAddress2: getMostCommonValueFromResult(result.data, 'PropertyAddress2') || prev.propertyAddress2,
          propertyCity: getMostCommonValueFromResult(result.data, 'PropertyCity') || prev.propertyCity,
          propertyState: getMostCommonValueFromResult(result.data, 'PropertyState') || prev.propertyState,
          propertyZip: getMostCommonValueFromResult(result.data, 'PropertyZip') || prev.propertyZip,
          propertyCounty: getMostCommonValueFromResult(result.data, 'PropertyCounty') || prev.propertyCounty,
          party1FirstName: getMostCommonValueFromResult(result.data, 'Borrower1FirstName') || prev.party1FirstName,
          party1MiddleName: getMostCommonValueFromResult(result.data, 'Borrower1MiddleName') || prev.party1MiddleName,
          party1LastName: getMostCommonValueFromResult(result.data, 'Borrower1LastName') || prev.party1LastName,
          party1Suffix: getMostCommonValueFromResult(result.data, 'Borrower1Suffix') || prev.party1Suffix,
          loanAmount: getMostCommonValueFromResult(result.data, 'LoanAmount') || prev.loanAmount
        }));

        alert('PDF analyzed successfully! Fields have been auto-filled with AI consensus.');
        setShowDataTraceSection(true);
        setShowUploadModal(false);
      }
    } catch (error) {
      console.error('AI analysis failed:', error);
      alert('Failed to analyze PDF: ' + error.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Helper function for initial auto-fill (before caseData state is set)
  const getMostCommonValueFromResult = (data, fieldName) => {
    const values = ['openai', 'claude', 'gemini']
      .map(provider => data[provider]?.[fieldName])
      .filter(v => !isValueEmpty(v));

    if (values.length === 0) return '';

    const valueCounts = values.reduce((acc, value) => {
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});

    const sortedValues = Object.entries(valueCounts).sort((a, b) => b[1] - a[1]);
    return sortedValues.length > 0 ? sortedValues[0][0] : '';
  };

  const handlePlaceOrder = () => {
    // Validate required fields
    if (!formData.agentFileNumber) {
      alert('Please enter an Agent File Number');
      return;
    }
    if (!formData.propertyAddress1 || !formData.propertyCity || !formData.propertyState) {
      alert('Please enter complete property address');
      return;
    }

    console.log('Placing Accelerate order:', formData);
    alert('Order placed successfully! (Demo mode)');
  };

  const handleCheckDataTrace = async () => {
    alert('DataTrace verification coming soon!');
  };

  const handleCreateInSoftPro = async () => {
    alert('SoftPro order creation coming soon!');
  };

  const handleChatWithFile = async () => {
    alert('Chat with this File feature coming soon!');
  };

  return (
    <div className="accelerate-integration">
      {/* Header with Logo and Demo Label */}
      <div className="accelerate-header">
        <img src="/stewart-logo.png" alt="Stewart" className="stewart-logo" />
        <span className="demo-mockup-label">Demo Mock-Up</span>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>AI Auto-Fill from PDF</h2>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Upload an order PDF or Accelerate report PDF to automatically extract and fill property information
              </p>

              <div
                className={`drop-zone ${isDragging ? 'dragging' : ''} ${uploadedFile ? 'has-file' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {uploadedFile ? (
                  <div className="uploaded-file">
                    <div className="file-icon">📄</div>
                    <div className="file-name">{uploadedFile.name}</div>
                    <button className="remove-file" onClick={handleRemoveFile}>Remove</button>
                  </div>
                ) : (
                  <div className="drop-zone-empty">
                    <div className="upload-icon">📤</div>
                    <p className="drop-text">Drag and drop PDF here</p>
                    <p className="drop-or">or</p>
                    <label htmlFor="file-input" className="browse-button">
                      Browse Files
                    </label>
                    <input
                      id="file-input"
                      type="file"
                      accept=".pdf"
                      onChange={handleFileInput}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
              </div>

              {uploadedFile && (
                <button
                  className="analyze-button"
                  onClick={handleAIAutoFill}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze & Auto-Fill'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sunny AI Capabilities Banner - Shows after AI auto-fill */}
      {showDataTraceSection && (
        <div className="sunny-banner">
          <div className="sunny-banner-content">
            <span className="sunny-banner-icon">✨</span>
            <span className="sunny-banner-text">Sunny AI Capabilities Available</span>
            <button className="sunny-banner-button" onClick={handleCheckDataTrace}>
              Check DataTrace
            </button>
            <button className="sunny-banner-button" onClick={handleCreateInSoftPro}>
              Create in SoftPro
            </button>
          </div>
        </div>
      )}

      {/* AI Provider Selector - Shows after AI auto-fill */}
      {showDataTraceSection && (
        <div className="sunny-banner" style={{ borderTop: '1px solid #444' }}>
          <div className="sunny-banner-content">
            <span className="sunny-banner-text">AI Providers for Consensus:</span>
            <div className="provider-toggles">
              <label className={`provider-toggle ${selectedProviders.includes('openai') ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedProviders.includes('openai')}
                  onChange={() => toggleProvider('openai')}
                />
                <span>OpenAI</span>
              </label>
              <label className={`provider-toggle ${selectedProviders.includes('claude') ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedProviders.includes('claude')}
                  onChange={() => toggleProvider('claude')}
                />
                <span>Claude</span>
              </label>
              <label className={`provider-toggle ${selectedProviders.includes('gemini') ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedProviders.includes('gemini')}
                  onChange={() => toggleProvider('gemini')}
                />
                <span>Gemini</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Mockup Notice */}
      <div className="accelerate-form">
        <div className="mockup-notice">
          <div className="mockup-icon">ℹ️</div>
          <div className="mockup-content">
            <div className="mockup-text">
              <h3>Demo Mockup</h3>
              <p>
                This is a demonstration mockup based on Stewart's current Accelerate web service.
                It showcases how Sunny's agentic AI capabilities can augment the product with intelligent
                PDF data extraction, multi-AI consensus validation, DataTrace property verification,
                and seamless integration with Title Production Software systems like SoftPro.
              </p>
            </div>
            <button
              className="view-sample-btn"
              onClick={() => window.open('/accelerate-current-portal.html', '_blank')}
            >
              🖼️ View Current Accelerate Portal
            </button>
          </div>
        </div>

        {/* Sunny Auto-Fill Button */}
        <div className="sunny-autofill-section">
          <button className="ai-upload-button-main" onClick={() => setShowUploadModal(true)}>
            <img src="/sunny-logo.svg" alt="Sunny AI" style={{ width: '24px', height: '24px', marginRight: '10px' }} />
            Auto-Fill from PDF
          </button>
        </div>

        {/* Property Address Section */}
        <div className="form-section">
          <h2 className="section-header">Property Address</h2>

          <div className="form-grid">
            <div className="form-group full-width">
              <label>Agent File Number *</label>
              <input
                type="text"
                value={formData.agentFileNumber}
                onChange={(e) => handleChange('agentFileNumber', e.target.value)}
                placeholder="Enter unique tracking ID"
              />
            </div>

            <div className="form-group">
              <label>Property Address *</label>
              <input
                type="text"
                value={formData.propertyAddress1}
                onChange={(e) => handleChange('propertyAddress1', e.target.value)}
                placeholder="Street address"
                className={getInputClass('propertyAddress1')}
              />
            </div>

            <div className="form-group">
              <label>Address 2</label>
              <input
                type="text"
                value={formData.propertyAddress2}
                onChange={(e) => handleChange('propertyAddress2', e.target.value)}
                placeholder="Unit, suite, etc."
                className={getInputClass('propertyAddress2')}
              />
            </div>

            <div className="form-group small">
              <label>Zip</label>
              <input
                type="text"
                value={formData.propertyZip}
                onChange={(e) => handleChange('propertyZip', e.target.value)}
                placeholder="ZIP"
                className={getInputClass('propertyZip')}
              />
            </div>

            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={formData.propertyCity}
                onChange={(e) => handleChange('propertyCity', e.target.value)}
                placeholder="City"
                className={getInputClass('propertyCity')}
              />
            </div>

            <div className="form-group">
              <label>State *</label>
              <select
                value={formData.propertyState}
                onChange={(e) => handleChange('propertyState', e.target.value)}
                className={getInputClass('propertyState')}
              >
                <option value="">Select state</option>
                <option value="MI">Michigan</option>
                <option value="OH">Ohio</option>
                <option value="IN">Indiana</option>
              </select>
            </div>

            <div className="form-group">
              <label>County</label>
              <input
                type="text"
                value={formData.propertyCounty}
                onChange={(e) => handleChange('propertyCounty', e.target.value)}
                placeholder="County"
                className={getInputClass('propertyCounty')}
              />
            </div>

            <div className="form-group">
              <label>Property Type</label>
              <select
                value={formData.propertyType}
                onChange={(e) => handleChange('propertyType', e.target.value)}
              >
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Title Agency</label>
              <input
                type="text"
                value={formData.titleAgency}
                readOnly
                className="readonly-field"
              />
            </div>
          </div>
        </div>

        {/* Parties Section */}
        <div className="form-section">
          <h2 className="section-header">Parties</h2>

          <div className="party-box">
            <div className="party-header">
              <span className="party-label">Party 1</span>
            </div>

            <div className="form-grid">
              <div className="form-group small">
                <label>Role</label>
                <select
                  value={formData.party1Role}
                  onChange={(e) => handleChange('party1Role', e.target.value)}
                >
                  <option value="Borrower">Borrower</option>
                  <option value="Seller">Seller</option>
                  <option value="Buyer">Buyer</option>
                </select>
              </div>

              <div className="form-group small">
                <label>Type</label>
                <select
                  value={formData.party1Type}
                  onChange={(e) => handleChange('party1Type', e.target.value)}
                >
                  <option value="Individual">Individual</option>
                  <option value="Entity">Entity</option>
                </select>
              </div>

              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={formData.party1FirstName}
                  onChange={(e) => handleChange('party1FirstName', e.target.value)}
                  placeholder="First name"
                  className={getInputClass('party1FirstName')}
                />
              </div>

              <div className="form-group small">
                <label>M.I.</label>
                <input
                  type="text"
                  value={formData.party1MiddleName}
                  onChange={(e) => handleChange('party1MiddleName', e.target.value)}
                  placeholder="M.I."
                  maxLength="1"
                  className={getInputClass('party1MiddleName')}
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={formData.party1LastName}
                  onChange={(e) => handleChange('party1LastName', e.target.value)}
                  placeholder="Last name"
                  className={getInputClass('party1LastName')}
                />
              </div>

              <div className="form-group small">
                <label>Suffix</label>
                <select
                  value={formData.party1Suffix}
                  onChange={(e) => handleChange('party1Suffix', e.target.value)}
                  className={getInputClass('party1Suffix')}
                >
                  <option value="">--</option>
                  <option value="Jr.">Jr.</option>
                  <option value="Sr.">Sr.</option>
                  <option value="III">III</option>
                  <option value="IV">IV</option>
                </select>
              </div>

              <div className="form-group full-width checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.sameAsPropertyAddress}
                    onChange={handleSameAsPropertyToggle}
                  />
                  <span>Same as Property Address</span>
                </label>
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.party1Address1}
                  onChange={(e) => handleChange('party1Address1', e.target.value)}
                  placeholder="Street address"
                  disabled={formData.sameAsPropertyAddress}
                />
              </div>

              <div className="form-group">
                <label>Address 2</label>
                <input
                  type="text"
                  value={formData.party1Address2}
                  onChange={(e) => handleChange('party1Address2', e.target.value)}
                  placeholder="Unit, suite, etc."
                  disabled={formData.sameAsPropertyAddress}
                />
              </div>

              <div className="form-group small">
                <label>Zip</label>
                <input
                  type="text"
                  value={formData.party1Zip}
                  onChange={(e) => handleChange('party1Zip', e.target.value)}
                  placeholder="ZIP"
                  disabled={formData.sameAsPropertyAddress}
                />
              </div>

              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.party1City}
                  onChange={(e) => handleChange('party1City', e.target.value)}
                  placeholder="City"
                  disabled={formData.sameAsPropertyAddress}
                />
              </div>

              <div className="form-group">
                <label>State</label>
                <select
                  value={formData.party1State}
                  onChange={(e) => handleChange('party1State', e.target.value)}
                  disabled={formData.sameAsPropertyAddress}
                >
                  <option value="">Select state</option>
                  <option value="MI">Michigan</option>
                  <option value="OH">Ohio</option>
                  <option value="IN">Indiana</option>
                </select>
              </div>

              <div className="form-group">
                <label>County</label>
                <input
                  type="text"
                  value={formData.party1County}
                  onChange={(e) => handleChange('party1County', e.target.value)}
                  placeholder="County"
                  disabled={formData.sameAsPropertyAddress}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="form-section">
          <h2 className="section-header">Products</h2>

          <div className="form-grid">
            <div className="form-group">
              <label>Product Type</label>
              <select
                value={formData.productType}
                onChange={(e) => handleChange('productType', e.target.value)}
              >
                <option value="Accelerate Title Report">Accelerate Title Report</option>
                <option value="Full Search">Full Search</option>
              </select>
            </div>

            <div className="form-group">
              <label>Transaction Type</label>
              <select
                value={formData.transactionType}
                onChange={(e) => handleChange('transactionType', e.target.value)}
              >
                <option value="Refinance">Refinance</option>
                <option value="Purchase">Purchase</option>
              </select>
            </div>

            <div className="form-group">
              <label>Loan Amount</label>
              <input
                type="text"
                value={formData.loanAmount}
                onChange={(e) => handleChange('loanAmount', e.target.value)}
                placeholder="$"
                className={getInputClass('loanAmount')}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="place-order-btn"
            onClick={handlePlaceOrder}
          >
            Place Order
          </button>
          <button
            type="button"
            className="create-softpro-btn"
            onClick={handleCreateInSoftPro}
          >
            <img src="/sunny-logo.svg" alt="Sunny AI" style={{ width: '20px', height: '20px', marginRight: '8px' }} />
            Create Order in TPS
          </button>
          <button
            type="button"
            className="chat-with-file-btn"
            onClick={handleChatWithFile}
          >
            <img src="/sunny-logo.svg" alt="Sunny AI" style={{ width: '20px', height: '20px', marginRight: '8px' }} />
            Chat with this File
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccelerateIntegration;
